/**
 * Traffic quality by country (R7, plan §10a.1) — INTERNAL.
 *
 * Page views ÷ visits is the datacentre tell: a reader reads more than one
 * page, a machine that fetches one page per "visit" sits at ~1.00. Singapore
 * was 44.7% of beacon page views on 2026-09-22, which is the open question
 * this answers. The public human-page-views figure is unchanged while it is
 * open — this panel is where the doubt lives.
 */
import { useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getMetricSeries } from '../../lib/api/analytics.js';
import { addDays, compact, toNumber } from '../../lib/analyticsShape.js';
import { VisibilityTag } from './StatTile.js';

/** At or below this, a country's "visits" look like single-page fetches. */
export const FLAT_RATIO = 1.05;
/** Only worth flagging when the country is a material share of the total. */
export const MATERIAL_SHARE = 0.05;

export interface CountryQuality {
  country: string;
  views: number;
  visits: number;
  ratio: number | null;
  share: number;
  flagged: boolean;
}

export function countryQuality(
  viewRows: Array<{ dimension_value: string; value: string | number }>,
  visitRows: Array<{ dimension_value: string; value: string | number }>,
): CountryQuality[] {
  const views = new Map<string, number>();
  const visits = new Map<string, number>();
  for (const r of viewRows) views.set(r.dimension_value, (views.get(r.dimension_value) ?? 0) + (toNumber(r.value) ?? 0));
  for (const r of visitRows) visits.set(r.dimension_value, (visits.get(r.dimension_value) ?? 0) + (toNumber(r.value) ?? 0));
  const total = Array.from(views.values()).reduce((a, b) => a + b, 0);
  return Array.from(views.entries())
    .map(([country, v]) => {
      const vis = visits.get(country) ?? 0;
      const ratio = vis > 0 ? v / vis : null;
      const share = total > 0 ? v / total : 0;
      return {
        country, views: v, visits: vis, ratio, share,
        flagged: ratio != null && ratio <= FLAT_RATIO && share >= MATERIAL_SHARE,
      };
    })
    .sort((a, b) => b.views - a.views);
}

export function TrafficQuality({ sites, to, stale }: { sites?: string[]; to: string; stale?: boolean }) {
  const from = addDays(to, -29);
  const common = { sites, dateFrom: from, dateTo: to, dimension: 'country' };
  const views = useQuery({
    queryKey: ['tq-views', from, to, sites],
    queryFn: () => getMetricSeries({ metric: 'rum.pageviews', ...common }),
    placeholderData: keepPreviousData, staleTime: 60_000,
  });
  const visits = useQuery({
    queryKey: ['tq-visits', from, to, sites],
    queryFn: () => getMetricSeries({ metric: 'rum.visits', ...common }),
    placeholderData: keepPreviousData, staleTime: 60_000,
  });

  const rows = useMemo(
    () => countryQuality(views.data?.rows ?? [], visits.data?.rows ?? []).slice(0, 10),
    [views.data, visits.data],
  );
  const noVisits = rows.length > 0 && rows.every((r) => r.visits === 0);
  const flagged = rows.filter((r) => r.flagged);

  return (
    <section className={`bg-surface rounded-xl border border-grey-200 p-4 flex flex-col gap-3 ${stale ? 'opacity-60' : ''} transition-opacity`} aria-label="Traffic quality">
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-grey-900">Traffic quality by country</h3>
          <p className="text-xs text-grey-500 mt-0.5">
            Beacon page views ÷ visits, {from} → {to}. A real reader reads more than one page; a country
            sitting at ~1.00 on a material share of traffic is the datacentre signature, not an audience.
          </p>
        </div>
        <VisibilityTag visibility="internal" />
      </header>
      {views.isLoading || visits.isLoading ? (
        <p className="text-sm text-grey-400 py-4 text-center">Loading…</p>
      ) : noVisits ? (
        <p className="text-sm text-grey-400 py-4 text-center">
          Visits per country are collected from 2026-09-23 — run the beacon lane's backfill to fill the history.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-grey-500">
                <th className="py-1 pr-3 font-medium">Country</th>
                <th className="py-1 pr-3 font-medium text-right">Page views</th>
                <th className="py-1 pr-3 font-medium text-right">Visits</th>
                <th className="py-1 pr-3 font-medium text-right">Views / visit</th>
                <th className="py-1 pr-3 font-medium text-right">Share</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.country} className={`border-t border-grey-100 ${r.flagged ? 'text-warning-700' : 'text-grey-900'}`}>
                  <td className="py-1 pr-3 font-medium">
                    {r.country}{r.flagged ? <span title="one page per visit on a material share — verify before quoting"> ⚠</span> : null}
                  </td>
                  <td className="py-1 pr-3 text-right tabular-nums">{compact(r.views)}</td>
                  <td className="py-1 pr-3 text-right tabular-nums">{compact(r.visits)}</td>
                  <td className="py-1 pr-3 text-right tabular-nums">{r.ratio == null ? '—' : r.ratio.toFixed(2)}</td>
                  <td className="py-1 pr-3 text-right tabular-nums text-grey-600">{(r.share * 100).toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <footer className="text-[11px] text-grey-400">
        Cloudflare beacon · ⚠ = {FLAT_RATIO.toFixed(2)} views per visit or fewer on {(MATERIAL_SHARE * 100).toFixed(0)}%+ of page views
        {flagged.length > 0 ? ` · flagged: ${flagged.map((f) => f.country).join(', ')} — the public figure still includes them (R7)` : ''}
      </footer>
    </section>
  );
}
